import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";
const API = `${process.env.REACT_APP_API_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter credentials");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("password", password);
      
      const res = await axios.post(`${API}/admin/login`, formData);
      
      // Store token for legacy support
      localStorage.setItem("speakr_admin_token", res.data.token);
      
      // Store admin data for AdminBoardContext
      const adminUser = {
        id: res.data.admin_id || "admin-1",
        email: email,
        name: res.data.name || "Admin",
        role: res.data.role || "gsk_super_admin",
        token: res.data.token
      };
      localStorage.setItem("speakr_admin", JSON.stringify(adminUser));
      
      toast.success("Welcome back, Admin!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error("Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  // Seed admin on first visit
  useEffect(() => {
    const seedAdmin = async () => {
      try {
        await axios.post(`${API}/admin/seed`);
      } catch (error) {
        console.log("Admin may already exist");
      }
    };
    seedAdmin();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="admin-login">
      <Card className="w-full max-w-md bg-card border-border">
        <CardHeader className="text-center">
          <img src={SPEAKR_LOGO} alt="Speakr" className="h-12 w-auto mx-auto mb-4" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-amber-500" />
            <CardTitle className="text-xl">Admin Portal</CardTitle>
          </div>
          <CardDescription>GSK Productions Control Center</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label>Admin Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@gskproductions.ca"
                data-testid="admin-email"
              />
            </div>
            <div>
              <Label>Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-password"
              />
              <div className="text-right mt-1">
                <Link to="/admin/forgot-password" className="text-sm text-blue-400 hover:underline" data-testid="admin-forgot-link">
                  Forgot password?
                </Link>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? "Logging in..." : "Access Admin Panel"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
          <p className="text-xs text-center text-muted-foreground mt-4">
            Authorized personnel only
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
